import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useSelector, useDispatch, useStore } from 'react-redux';
import { JSONTree } from 'react-json-tree';
import { Link } from 'react-router-dom';
import { Alert, Spinner } from '@amzn/awsui-components-react-v2';
import {
    Table,
    Header,
    Pagination,
    CollectionPreferences,
    TextFilter,
    SegmentedControl,
    ButtonDropdown,
    TableProps,
} from '@amzn/awsui-components-react-v3';

import {
    AJAX_CALL_ERROR,
    LOADING_TEXT,
    AUDIT_EMPTY,
    AUDIT_PAGE_SIZE,
} from '../../constants/grimsby';
import { formatTimestampUTC } from '../../utils/formatDate';
import getPhoneToolUrl from '../../utils/getPhoneToolUrl';
import downloadBlob from '../../utils/downloadBlob';
import { useNotifications } from '../../context/grimsbyNotifications';

import {
    selectItems,
    selectNextToken,
    selectError,
    selectIsLoading,
    selectIsLoaded,
    getAuditTrail,
    fetchAllAuditPages,
    setIsLoaded,
    setError,
    resetItems,
} from '../../store/slices/auditTrailSlice';
import { AuditItemData } from '../../interfaces/auditItem';
import { computeDiff, DiffEntry } from './auditDiff';
import { GlobalState } from '../../../main/store';

const PAGE_SIZE_KEY = 'grimsby-audit-page-size';
const VIEW_MODE_KEY = 'grimsby-audit-view-mode';
const PAGE_SIZE_OPTIONS = [25, 50, 75, 100];
const DEFAULT_PAGE_SIZE = 25;

type ViewMode = 'tree' | 'flat';

function getStoredPageSize(): number {
    try {
        const stored = parseInt(localStorage.getItem(PAGE_SIZE_KEY) || '0', 10);
        if (PAGE_SIZE_OPTIONS.includes(stored)) return stored;
    } catch {
        // localStorage unavailable
    }
    return DEFAULT_PAGE_SIZE;
}

function setStoredPageSize(size: number): void {
    try {
        localStorage.setItem(PAGE_SIZE_KEY, String(size));
    } catch {
        // localStorage unavailable
    }
}

function getStoredViewMode(): ViewMode {
    try {
        const stored = localStorage.getItem(VIEW_MODE_KEY);
        if (stored === 'tree' || stored === 'flat') return stored;
    } catch {
        // localStorage unavailable
    }
    return 'tree';
}

function setStoredViewMode(mode: ViewMode): void {
    try {
        localStorage.setItem(VIEW_MODE_KEY, mode);
    } catch {
        // localStorage unavailable
    }
}

const auditJsonTheme = {
    scheme: 'auditTheme',
    base00: 'transparent', // background color
    base01: '#3C444F',
    base02: '#4F5A65',
    base03: '#BEBEBE',
    base04: '#b0b0b0',
    base05: '#d0d0d0',
    base06: '#FFFFFF',
    base07: '#f5f5f5',
    base08: '#A1C659', // null values
    base09: '#6FB3D2', // timestamps, booleans
    base0A: '#ddb26f',
    base0B: '#fd971f', // strings
    base0C: '#12cfc0',
    base0D: '#fafafa', // keys
    base0E: '#D381C3',
    base0F: '#deaf8f',
};

const diffStyles = {
    row: {
        fontFamily: 'monospace',
        fontSize: '12px',
        lineHeight: '1.6',
        padding: '2px 0',
        display: 'block' as const,
    },
    added: {
        backgroundColor: '#e6ffec',
        padding: '1px 4px',
        borderRadius: '3px',
    },
    removed: {
        backgroundColor: '#ffebe9',
        padding: '1px 4px',
        borderRadius: '3px',
        textDecoration: 'line-through' as const,
    },
    key: { fontWeight: 600, marginRight: '4px' },
};

function formatValue(val: unknown): string {
    if (val === null || val === undefined) return 'null';
    if (typeof val === 'object') return JSON.stringify(val);
    return String(val);
}

const DiffCell = ({ item }: { item: AuditItemData }) => {
    const diffs = useMemo(() => {
        const old = item.old_value as Record<string, unknown>;
        const neu = item.new_value as Record<string, unknown>;
        return computeDiff(old, neu, '', true);
    }, [item]);
    if (diffs.length === 0) {
        return <span style={{ color: '#888' }}>No changes</span>;
    }
    return (
        <div
            data-testid="audit-diff-cell"
            style={{ maxHeight: '240px', overflow: 'auto' }}
        >
            {diffs.map((d: DiffEntry) => (
                <span key={d.path} style={diffStyles.row}>
                    {d.type === 'added' && (
                        <>
                            <span style={diffStyles.key}>{d.path}:</span>
                            <span style={diffStyles.added}>
                                + {formatValue(d.newVal)}
                            </span>
                        </>
                    )}
                    {d.type === 'removed' && (
                        <>
                            <span style={diffStyles.key}>{d.path}:</span>
                            <span style={diffStyles.removed}>
                                - {formatValue(d.oldVal)}
                            </span>
                        </>
                    )}
                    {d.type === 'changed' && (
                        <>
                            <span style={diffStyles.key}>{d.path}:</span>
                            <span style={diffStyles.removed}>
                                {formatValue(d.oldVal)}
                            </span>
                            {' \u2192 '}
                            <span style={diffStyles.added}>
                                {formatValue(d.newVal)}
                            </span>
                        </>
                    )}
                    {d.type === 'unchanged' && (
                        <>
                            <span style={diffStyles.key}>{d.path}:</span>
                            <span>{formatValue(d.newVal)}</span>
                        </>
                    )}
                </span>
            ))}
        </div>
    );
};

function getColumnDefinitions(
    viewMode: ViewMode,
    expandAll: boolean,
    expandKey: number,
): Array<TableProps.ColumnDefinition<AuditItemData>> {
    const cols: Array<TableProps.ColumnDefinition<AuditItemData>> = [
        {
            id: 'modified_by',
            header: 'Modified By',
            cell: (item: AuditItemData) => (
                <Link
                    to={{
                        pathname: getPhoneToolUrl(item.resource_modified_by),
                    }}
                    target="_blank"
                >
                    {item.resource_modified_by}
                </Link>
            ),
        },
        {
            id: 'timestamp',
            header: 'Timestamp',
            cell: (item: AuditItemData) =>
                formatTimestampUTC(item.resource_modified_timestamp),
        },
    ];

    if (viewMode === 'flat') {
        cols.push({
            id: 'diff',
            header: 'Changes',
            cell: (item: AuditItemData) => <DiffCell item={item} />,
        });
    } else {
        cols.push(
            {
                id: 'new_value',
                header: 'New Value',
                cell: (item: AuditItemData) => (
                    <JSONTree
                        key={`new_${expandKey}`}
                        data={item.new_value}
                        theme={auditJsonTheme}
                        shouldExpandNode={() => expandAll}
                    />
                ),
            },
            {
                id: 'old_value',
                header: 'Old Value',
                cell: (item: AuditItemData) => (
                    <JSONTree
                        key={`old_${expandKey}`}
                        data={item.old_value}
                        theme={auditJsonTheme}
                        shouldExpandNode={() => expandAll}
                    />
                ),
            },
        );
    }
    return cols;
}

function extractSearchable(obj: unknown): string[] {
    if (obj === null || obj === undefined) return [];
    if (typeof obj !== 'object') return [String(obj)];
    const results: string[] = [];
    for (const [key, val] of Object.entries(obj as Record<string, unknown>)) {
        results.push(key);
        if (val === null || val === undefined) continue;
        if (typeof val === 'object') {
            results.push(...extractSearchable(val));
        } else {
            results.push(String(val));
        }
    }
    return results;
}

// Filter predicate shared by the on-screen table and the export path.
function itemMatchesFilter(item: AuditItemData, query: string): boolean {
    if (item.resource_modified_by.toLowerCase().includes(query)) return true;
    const values = extractSearchable(item.new_value).concat(
        extractSearchable(item.old_value),
    );
    return values.some((v) => v.toLowerCase().includes(query));
}

const AuditTab = ({ id }: { id: string }) => {
    const error = useSelector(selectError);
    const isLoading = useSelector(selectIsLoading);
    const items = useSelector(selectItems);
    const nextToken = useSelector(selectNextToken);
    const isLoaded = useSelector(selectIsLoaded);
    const dispatch = useDispatch();
    const store = useStore<GlobalState>();
    const { addNotification } = useNotifications();
    // Guards against concurrent export runs: a second click while a drain is
    // in flight would start an overlapping fetchAllAuditPages loop that shares
    // and corrupts the same store paging state (nextToken/items).
    const isExportingRef = useRef(false);

    const [pageSize, setPageSize] = useState<number>(getStoredPageSize);
    const [currentPage, setCurrentPage] = useState(1);
    const [viewMode, setViewMode] = useState<ViewMode>(getStoredViewMode);
    const [filterText, setFilterText] = useState('');
    const [expandAll, setExpandAll] = useState(false);
    // Bumped on every Expand/Collapse click. Used as a JSONTree remount key so
    // clicking the same option twice (or re-expanding after user manually
    // collapsed a node) re-applies the expansion. shouldExpandNode alone is
    // only consulted on initial mount, so a key change is required.
    const [expandKey, setExpandKey] = useState(0);

    useEffect(() => {
        setCurrentPage(1);
        dispatch(resetItems());
        dispatch(getAuditTrail(id, pageSize));
        return () => {
            dispatch(setIsLoaded(false));
            dispatch(resetItems());
        };
    }, [id, dispatch, pageSize]);

    // Auto-fetch all pages while a search filter is active
    useEffect(() => {
        if (filterText.trim() && nextToken !== null && !isLoading) {
            dispatch(getAuditTrail(id, pageSize));
        }
    }, [filterText, nextToken, isLoading, id, dispatch, pageSize]);

    const columnDefinitions = useMemo(
        () => getColumnDefinitions(viewMode, expandAll, expandKey),
        [viewMode, expandAll, expandKey],
    );

    const filteredItems = useMemo(() => {
        if (!filterText.trim()) return items;
        const query = filterText.toLowerCase();
        return items.filter((item) => itemMatchesFilter(item, query));
    }, [items, filterText]);

    const handlePageSizeChange = (size: number) => {
        setPageSize(size);
        setStoredPageSize(size);
        setCurrentPage(1);
    };

    const handleViewModeChange = (mode: ViewMode) => {
        setViewMode(mode);
        setStoredViewMode(mode);
    };

    const buildExportRows = (
        rowItems: AuditItemData[],
    ): Array<{
        modifiedBy: string;
        timestamp: string;
        newValue: string;
        oldValue: string;
    }> => {
        // Collapse any embedded newlines in the JSON payload so CSV/TSV rows
        // stay on a single line. JSON.stringify does not emit raw newlines
        // today, but a nested string value containing '\n' would.
        const oneLine = (s: string) => s.replace(/[\r\n]+/g, ' ');
        return rowItems.map((item) => ({
            modifiedBy: item.resource_modified_by,
            timestamp: formatTimestampUTC(item.resource_modified_timestamp),
            newValue: oneLine(JSON.stringify(item.new_value)),
            oldValue: oneLine(JSON.stringify(item.old_value)),
        }));
    };

    // Fetch all remaining pages (same paging loop as the search filter), then
    // read the freshly-loaded items from the store (the component's
    // items/filteredItems are stale inside the async handler until re-render)
    // and apply the active filter so export matches what search would show.
    const getExportItems = async (): Promise<AuditItemData[]> => {
        const ok = await dispatch(fetchAllAuditPages(id, pageSize));
        if (!ok) {
            // A page fetch failed mid-drain. Clear the transient error so the
            // tab keeps showing the loaded table (not the error Alert), and
            // signal the caller to abort rather than export partial data.
            dispatch(setError(null));
            throw new Error('Failed to load all audit pages for export');
        }
        const allItems = store.getState().auditTrail.items;
        const query = filterText.trim().toLowerCase();
        if (!query) return allItems;
        return allItems.filter((item) => itemMatchesFilter(item, query));
    };

    const handleCopyToClipboard = async () => {
        if (isExportingRef.current) return;
        isExportingRef.current = true;
        try {
            const exportItems = await getExportItems();
            const header = [
                'Modified By',
                'Timestamp',
                'New Value',
                'Old Value',
            ].join('\t');
            const rows = buildExportRows(exportItems).map((r) =>
                [r.modifiedBy, r.timestamp, r.newValue, r.oldValue].join('\t'),
            );
            await navigator.clipboard.writeText([header, ...rows].join('\n'));
            addNotification({
                id: `audit-copy-success-${Date.now()}`,
                type: 'success',
                content: 'Audit log copied to clipboard.',
                dismissible: true,
            });
        } catch {
            // Either the drain failed (incomplete data) or the clipboard write
            // was rejected. Either way, do not present a partial success.
            addNotification({
                id: `audit-copy-error-${Date.now()}`,
                type: 'error',
                content: 'Could not copy the audit log.',
                dismissible: true,
            });
        } finally {
            isExportingRef.current = false;
        }
    };

    const csvEscape = (val: string): string => {
        // Neutralize CSV formula injection: a leading =, +, -, @ (or a leading
        // tab/CR) makes spreadsheets treat the cell as a formula. Prefix with a
        // single quote so the value is treated as text. resource_modified_by
        // and JSON payload values are user-controlled.
        let out = val;
        if (/^[=+\-@\t\r]/.test(out)) {
            out = `'${out}`;
        }
        // Quote per RFC 4180 only when the value contains a comma, quote, or
        // newline. Unquoted values are legal and cheaper for typical fields.
        if (!/[",\r\n]/.test(out)) return out;
        return `"${out.replace(/"/g, '""')}"`;
    };

    const handleExportCsv = async () => {
        if (isExportingRef.current) return;
        isExportingRef.current = true;
        try {
            const exportItems = await getExportItems();
            const header = [
                'Modified By',
                'Timestamp',
                'New Value',
                'Old Value',
            ]
                .map(csvEscape)
                .join(',');
            const rows = buildExportRows(exportItems).map((r) =>
                [r.modifiedBy, r.timestamp, r.newValue, r.oldValue]
                    .map(csvEscape)
                    .join(','),
            );
            const csv = [header, ...rows].join('\n');
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            downloadBlob(blob, `audit_log_${id}.csv`);
        } catch {
            // Drain failed (incomplete data) or the download failed -- do not
            // hand the user a partial CSV silently.
            addNotification({
                id: `audit-export-error-${Date.now()}`,
                type: 'error',
                content: 'Could not export the audit log to CSV.',
                dismissible: true,
            });
        } finally {
            isExportingRef.current = false;
        }
    };

    if (error) {
        return (
            <Alert data-testid="AuditTabError" header="Error" type="error">
                {AJAX_CALL_ERROR}
            </Alert>
        );
    } else if (isLoaded) {
        const pagesCount = Math.max(
            1,
            Math.ceil(filteredItems.length / pageSize),
        );
        const safeCurrentPage = Math.min(currentPage, pagesCount);
        const paginatedItems = filteredItems.slice(
            (safeCurrentPage - 1) * pageSize,
            safeCurrentPage * pageSize,
        );

        return (
            <div data-testid="AuditTabDetails" className="audit-table">
                {/* View-mode controls (SegmentedControl + Display) live above the
                    Table, separate from data operations (Actions) which live in
                    the Table Header. This keeps view controls closer to the
                    content they affect. */}
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        marginBottom: '12px',
                        gap: '12px',
                    }}
                >
                    {viewMode === 'tree' && (
                        <ButtonDropdown
                            data-testid="AuditDisplay"
                            items={[
                                {
                                    id: 'expand-all',
                                    text: 'Expand all rows',
                                    itemType: 'checkbox',
                                    checked: expandAll,
                                },
                                {
                                    id: 'collapse-all',
                                    text: 'Collapse all rows',
                                    itemType: 'checkbox',
                                    checked: !expandAll,
                                },
                            ]}
                            onItemClick={({ detail }) => {
                                if (detail.id === 'expand-all') {
                                    setExpandAll(true);
                                } else if (detail.id === 'collapse-all') {
                                    setExpandAll(false);
                                }
                                setExpandKey((k) => k + 1);
                            }}
                        >
                            Display
                        </ButtonDropdown>
                    )}
                    <SegmentedControl
                        selectedId={viewMode}
                        onChange={({ detail }) =>
                            handleViewModeChange(detail.selectedId as ViewMode)
                        }
                        label="View mode"
                        options={[
                            { id: 'tree', text: 'Tree view' },
                            { id: 'flat', text: 'Flat view' },
                        ]}
                    />
                </div>
                <Table
                    columnDefinitions={columnDefinitions}
                    loadingText={LOADING_TEXT}
                    loading={isLoading}
                    items={paginatedItems}
                    header={
                        <Header
                            actions={
                                <ButtonDropdown
                                    data-testid="AuditActions"
                                    items={[
                                        {
                                            id: 'export-group',
                                            text: 'Export',
                                            items: [
                                                {
                                                    id: 'copy-clipboard',
                                                    text: 'Copy to clipboard',
                                                },
                                                {
                                                    id: 'export-csv',
                                                    text: 'Export to CSV',
                                                },
                                            ],
                                        },
                                    ]}
                                    onItemClick={({ detail }) => {
                                        if (detail.id === 'copy-clipboard') {
                                            handleCopyToClipboard();
                                        } else if (detail.id === 'export-csv') {
                                            handleExportCsv();
                                        }
                                    }}
                                >
                                    Actions
                                </ButtonDropdown>
                            }
                        >
                            Audit Log
                        </Header>
                    }
                    filter={
                        <TextFilter
                            filteringPlaceholder="Search audit entries..."
                            filteringText={filterText}
                            onChange={({ detail }) => {
                                setFilterText(detail.filteringText);
                                setCurrentPage(1);
                            }}
                            countText={
                                filterText.trim()
                                    ? nextToken !== null
                                        ? `${filteredItems.length} matches (searching...)`
                                        : `${filteredItems.length} matches`
                                    : undefined
                            }
                        />
                    }
                    empty={
                        <div className="awsui-util-t-c">
                            <div className="awsui-util-pt-s awsui-util-mb-xs">
                                <b>{AUDIT_EMPTY}</b>
                            </div>
                        </div>
                    }
                    pagination={
                        <Pagination
                            currentPageIndex={safeCurrentPage}
                            pagesCount={pagesCount}
                            openEnd={nextToken !== null}
                            onChange={({ detail }) => {
                                const requestedPage = detail.currentPageIndex;
                                const maxLoadedPage = Math.ceil(
                                    items.length / pageSize,
                                );
                                if (
                                    requestedPage > maxLoadedPage &&
                                    nextToken !== null
                                ) {
                                    dispatch(getAuditTrail(id, pageSize));
                                }
                                setCurrentPage(requestedPage);
                            }}
                        />
                    }
                    preferences={
                        <CollectionPreferences
                            title="Preferences"
                            confirmLabel="Confirm"
                            cancelLabel="Cancel"
                            preferences={{ pageSize }}
                            pageSizePreference={{
                                title: 'Page size',
                                options: PAGE_SIZE_OPTIONS.map((size) => ({
                                    value: size,
                                    label: `${size} items`,
                                })),
                            }}
                            onConfirm={({ detail }) => {
                                if (detail.pageSize)
                                    handlePageSizeChange(detail.pageSize);
                            }}
                        />
                    }
                />
            </div>
        );
    } else {
        return <Spinner data-testid="AuditTabLoadingSpinner" />;
    }
};

export default AuditTab;
